import { call, put, take, takeEvery, all, select } from 'redux-saga/effects'
import decamelizeKeysDeep from 'decamelize-keys-deep'

import * as Actions from 'actions/alerts'
import * as Api from 'api/alerts'
import { genericErrorMessage, genericSuccessMessage, exception } from 'actions/app'
import * as NavigationActions from 'actions/navigation'
import * as NewsActions from 'actions/news'
import * as SavedSearchesActions from 'actions/saved_searches'
import * as Selectors from 'selectors'
import { executeSavedSearch as executeSavedSearchSaga } from 'sagas/saved_searches'
import { savedSearchExecutionRoute } from 'utils/alerts'

const alertBody = alert => {
  const body = decamelizeKeysDeep(alert.toJS())

  delete body.cursor
  delete body.scheduled_jobs
  delete body.created_at
  delete body.updated_at
  delete body.newsradar_id
  delete body.created_by
  delete body.updated_by

  return body
}

export function* fetchAlerts() {
  try {
    const result = yield call(Api.fetchAlerts)
    yield put(Actions.fetchAlertsSuccess(result.alerts || []))
  } catch (error) {
    yield put(Actions.fetchAlertsError(error))
    yield put(exception(error))
    yield put(genericErrorMessage())
  }
}

export function* fetchAlertHistory() {
  try {
    const result = yield call(Api.fetchHistory)
    yield put(Actions.fetchAlertHistorySuccess(result.events || []))
  } catch (error) {
    yield put(Actions.fetchAlertHistoryError(error))
    yield put(exception(error))
    yield put(genericErrorMessage())
  }
}

export function* saveAlert({ payload: alert }) {
  try {
    const body = alertBody(alert)
    let result

    if (alert.get('id')) {
      result = yield call(Api.updateAlert, alert.get('id'), body)
    } else {
      result = yield call(Api.createAlert, body)
    }

    yield put(Actions.saveAlertSuccess(result))
    yield put(Actions.hideAlertDialog())
    yield put(genericSuccessMessage())
  } catch (error) {
    yield put(Actions.saveAlertError(error))
    yield put(exception(error))
    yield put(genericErrorMessage())
  }
}

export function* updateAlertsStatus({ payload: { alerts, enabled } }) {
  try {
    const alertIds = alerts.map(alert => alert.get('id'))
    const result = yield call(Api.updateAlertsStatus, alertIds, enabled)

    yield put(Actions.updateAlertsStatusSuccess(result.alerts || []))
    yield put(genericSuccessMessage())
  } catch (error) {
    yield put(Actions.updateAlertsStatusError(error))
    yield put(exception(error))
    yield put(genericErrorMessage())
  }
}

export function* deleteAlert({ payload: alertId }) {
  try {
    yield call(Api.deleteAlert, alertId)
    yield put(Actions.deleteAlertSuccess(alertId))
    yield put(Actions.hideRemoveAlertDialog())
    yield put(genericSuccessMessage())
  } catch (error) {
    yield put(Actions.deleteAlertError(error))
    yield put(exception(error))
    yield put(genericErrorMessage())
  }
}

export function* deleteAlerts({ payload: alertIds }) {
  try {
    const result = yield call(Api.deleteAlerts, alertIds)

    yield put(Actions.deleteAlertsSuccess(result.ids || alertIds))
    yield put(genericSuccessMessage())
  } catch (error) {
    yield put(Actions.deleteAlertsError(error))
    yield put(exception(error))
    yield put(genericErrorMessage())
  }
}

export function* executeAlertSavedSearch({ payload: savedSearch }) {
  const route = savedSearchExecutionRoute(savedSearch)
  yield put(NavigationActions.navigate(route))
  yield take(NewsActions.viewConfigPresetSelected)

  const savedSearchResolving = yield select(Selectors.getSavedSearchResolving)

  if (savedSearchResolving) {
    yield take(SavedSearchesActions.setExecutedSavedSearch)
  }

  yield call(executeSavedSearchSaga, SavedSearchesActions.executeSavedSearch(savedSearch))
}

export function* previewAlert({ payload: alert }) {
  try {
    const result = yield call(Api.previewAlert, alert.get('id'))
    yield put(Actions.previewAlertSuccess(result.results))
  } catch (error) {
    yield put(Actions.previewAlertError(error))
    yield put(exception(error))
    yield put(genericErrorMessage())
  }
}

export function* watchFetchAlerts() {
  yield takeEvery(Actions.fetchAlertsStart, fetchAlerts)
}

export function* watchFetchAlertHistory() {
  yield takeEvery(Actions.fetchAlertHistoryStart, fetchAlertHistory)
}

export function* watchSaveAlert() {
  yield takeEvery(Actions.saveAlertStart, saveAlert)
}

export function* watchUpdateAlertsStatus() {
  yield takeEvery(Actions.updateAlertsStatusStart, updateAlertsStatus)
}

export function* watchDeleteAlert() {
  yield takeEvery(Actions.deleteAlertStart, deleteAlert)
}

export function* watchDeleteAlerts() {
  yield takeEvery(Actions.deleteAlertsStart, deleteAlerts)
}

export function* watchExecuteAlertSavedSearch() {
  yield takeEvery(Actions.executeAlertSavedSearch, executeAlertSavedSearch)
}

export function* watchPreviewAlert() {
  yield takeEvery(Actions.previewAlertStart, previewAlert)
}

export default function* alertsSaga() {
  yield all([
    watchFetchAlerts(),
    watchFetchAlertHistory(),
    watchSaveAlert(),
    watchUpdateAlertsStatus(),
    watchDeleteAlert(),
    watchDeleteAlerts(),
    watchExecuteAlertSavedSearch(),
    watchPreviewAlert()
  ])
}
